import type { Abholauskunft, Anhaengervorschlag, Gespannvorschlag } from "./typen";
import type { Merkposten, Merkwunsch, Teilposten } from "./merkliste";

/* ══════════════════════════════════════════════════════════════════
   „Kunde holt ab" — was der Schalter an einem Posten bewegt.

   Luca, 18.09.2026: *„Wir haben ja beim Vermieten automatisch immer die
   Anfahrtspauschale mit drin … Gibt es aber ja auch den Fall, dass
   Maschinen abgeholt werden vom Kunden … Da muss man hier in der
   Merkliste dann direkt einen Schnellwahlbutton haben: ‚Kunde holt ab'
   … Das im Hintergrund löscht dann einfach die Anlieferung mit 65 €."*

   **Zwei Dinge passieren, und nur zwei:**

     · Der Zuschlag, der an der Anlieferung hängt, wird abgewählt.
     · Der Anhänger, den die Gruppe vorschlägt, kommt als eigener Posten
       dazu.

   ── Warum die Anlieferung abgewählt und nicht weggerechnet wird ────

   Die Merkliste kann Zuschläge seit dem 17.09.2026 abwählen, und der
   ganze Weg dahinter steht schon: Eine abgewählte Zeile geht als eigene
   Zuschlagszeile mit 0,00 in den Vorgang und schlägt dort die Regel
   desselben Codes (`MerklisteAbschluss.tsx`, `packages/fach/abrechnung.ts`).
   Ein zweiter Weg daneben wäre eine zweite Wahrheit — und die erste, die
   beim nächsten Umbau auseinanderläuft.

   **Welcher Zuschlag das ist, sagt der Stamm und nicht diese Datei**
   (`surcharge_rule.drops_on_self_pickup`, Migration 0127). Stünde hier
   `ANLIEF`, müsste der nächste Betrieb mit einer zweiten Fahrtpauschale
   sie im Browser nachtragen. Luca, 15.09.2026, zu genau dieser Sorte
   Bindung: *„Das muss halt generisch funktionieren."*

   ── Und warum der Rückweg zählt ────────────────────────────────────

   Wer den Schalter wieder ausmacht, bekommt die Anlieferung zurück —
   **aber nur die, die der Schalter weggenommen hat.** Deshalb trägt die
   abgewählte Zeile mit, warum sie aus ist (`ausWegenAbholung`). Einen
   Zuschlag, den jemand selbst abgewählt hat, schaltet ein Schalter
   daneben nicht ungefragt wieder an.

   ── Der zweite Anlass: das Gespann (18.09.2026) ────────────────────

   Migration 0072 kennt Gespanne: Wer eine Zugmaschine mietet, braucht den
   Auflieger, der hinter sie gehört. **Das ist dieselbe Mechanik für einen
   anderen Anlass** — ein zweites Gerät mit eigenem Preis und eigener
   Belegung, das an einem Posten hängt (`wegen`) und mit ihm wieder geht.
   Deshalb steht es hier und nicht in einer zweiten Datei: Der Unterschied
   sind zwei Sätze, die Gemeinsamkeit ist alles andere.

   | | Anhänger (0127) | Auflieger (0072) |
   |---|---|---|
   | Anlass | der Kunde holt selbst ab | die Zugmaschine fährt nicht allein |
   | Was noch passiert | die Anlieferung fällt weg | nichts |
   | Woran er hängt | `anhaenger` am Posten | `gespann` am Posten |
   | `wegenArt` | `abholung` | `gespann` |

   **Beide dürfen zugleich anstehen.** Eine Zugmaschine, die der Kunde
   selbst holt, braucht keinen Transportanhänger und trotzdem ihren
   Auflieger — darum unterscheidet `wegenArt` die beiden, statt dass jeder
   Schalter den Posten des anderen fände und beim Ausschalten mitnähme.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const txt = (wert: unknown): string => (typeof wert === "string" ? wert : "");

/**
 * Die Abholauskunft aus einer API-Antwort — abholbar, und welcher Anhänger
 * dann vorgeschlagen wird (Migration 0127).
 *
 * **Ein Vorschlag ohne beide Kennungen ist keiner.** Fehlt der Typ, ließe
 * sich kein Posten anlegen; fehlt die Ausstattung, träfe der Preis den
 * Anhänger, den jemand **ohne** Maschine mietet — 40,00 je Tag statt 15,00
 * je Miete (0102).
 */
export function alsAbholung(roh: unknown): Abholauskunft {
  const x = (roh && typeof roh === "object" ? roh : {}) as Roh;
  const a = (x["anhaenger"] && typeof x["anhaenger"] === "object"
    ? x["anhaenger"]
    : {}) as Roh;
  const varianteId = txt(a["variante_id"]);
  const typId = txt(a["typ_id"]);
  const ebene = typeof x["ebene"] === "number" ? x["ebene"] : null;
  return {
    abholbar: x["abholbar"] === true,
    ebene: ebene === 0 || ebene === 1 || ebene === 2 ? ebene : null,
    traeger: txt(x["traeger"]) || null,
    anhaenger: varianteId && typId
      ? {
        variante_id: varianteId,
        variante: txt(a["variante"]),
        typ_id: typId,
        typ: txt(a["typ"]),
      }
      : null,
  };
}

/**
 * Der Gespannvorschlag aus einer API-Antwort (Migration 0072).
 *
 * **Ohne Kennung ist es keiner.** Ein Vorschlag, aus dem sich kein Posten
 * anlegen lässt, ist ein Wort ohne Folge — dann steht der Schalter besser
 * gar nicht da.
 */
export function alsGespann(roh: unknown): Gespannvorschlag | null {
  const x = (roh && typeof roh === "object" ? roh : {}) as Roh;
  const typId = txt(x["typ_id"]);
  return typId ? { typ_id: typId, typ: txt(x["typ"]) } : null;
}

/**
 * **Steht der Schalter „Kunde holt ab" an diesem Posten?** (Systemprüfung
 * A-02, 24.09.2026)
 *
 * Lisa: Beim dritten Gerät, einem Minibagger, gab es *„gar kein Feld, um
 * ‚Kunde holt ab' anzugeben"*. Zwei Ursachen, beide im Code und nicht im
 * Stamm:
 *
 *   · Ein Posten, der **ohne Preis** gemerkt wurde (die Auskunft war noch
 *     unterwegs, oder er kam aus einer Wiederholung), bekam die Abholbarkeit
 *     nie — das Nachrechnen trug Miete und Zuschläge nach, sie nicht
 *     (`abholungAusPreis`).
 *   · Und der Schalter hing **nur** an der Gruppe. Wo die Anlieferung an der
 *     Karte steht, fällt aber eine Fahrt an — und wer selbst abholt, spart
 *     genau die, ob die Gruppe als „abholbar" gepflegt ist oder nicht.
 *
 * Deshalb steht er, wo die Gruppe es sagt **oder** wo ein Zuschlag an der
 * Karte hängt, der bei Selbstabholung entfällt. Den Anhänger schlägt er nur
 * dort vor, wo die Gruppe einen hinterlegt hat.
 */
export function abholungMoeglich(p: Merkposten): boolean {
  if (p.wegen) return false;
  return p.abholbar === true || p.zuschlaege.some((z) => z.faelltBeiAbholung === true);
}

/**
 * **Was die Preisauskunft über Abholung und Gespann weiß, geht an den Posten**
 * — bei jedem Nachrechnen, nicht nur beim Merken (Systemprüfung A-02).
 *
 * `undefined` nimmt einen Wert weg (`postenAendern`): Sagt der Stamm heute
 * „nicht abholbar", steht der Schalter auch nicht mehr aus der Zeit, als er es
 * war.
 */
export function abholungAusPreis(preis: {
  abholung?: Abholauskunft | undefined;
  gespann?: Gespannvorschlag | null | undefined;
}): Teilposten {
  return {
    abholbar: preis.abholung?.abholbar ? true : undefined,
    anhaenger: preis.abholung?.anhaenger ? alsAnhaenger(preis.abholung.anhaenger) : undefined,
    gespann: preis.gespann ? alsAuflieger(preis.gespann) : undefined,
  };
}

/** Trägt der Posten schon, was `abholungAusPreis` ihm geben würde? */
export function abholungSteht(p: Merkposten, teil: Teilposten): boolean {
  return (p.abholbar === true) === (teil.abholbar === true)
    && JSON.stringify(p.anhaenger ?? null) === JSON.stringify(teil.anhaenger ?? null)
    && JSON.stringify(p.gespann ?? null) === JSON.stringify(teil.gespann ?? null);
}

/** Der Vorschlag in der Form, in der ein Posten ihn trägt. */
export function alsAuflieger(vorschlag: Gespannvorschlag): NonNullable<Merkposten["gespann"]> {
  return { typId: vorschlag.typ_id, typ: vorschlag.typ };
}

/** Der Vorschlag in der Form, in der ein Posten ihn trägt. */
export function alsAnhaenger(vorschlag: Anhaengervorschlag): NonNullable<Merkposten["anhaenger"]> {
  return {
    typId: vorschlag.typ_id,
    typ: vorschlag.typ,
    varianteId: vorschlag.variante_id,
    variante: vorschlag.variante,
  };
}

/**
 * Was der Schalter am Posten selbst ändert — der Schalter an und aus.
 *
 * Die Zuschläge werden **umgelegt und nicht neu geholt**: Welche Zeile bei
 * Selbstabholung entfällt, steht schon an ihr (`faelltBeiAbholung`, aus dem
 * Stamm). Eine neue Abfrage für eine Antwort, die dasteht, wäre ein Umlauf
 * für nichts — und am Telefon eine Wartezeit mitten im Satz.
 */
export function abholungUmlegen(p: Merkposten, an: boolean): Teilposten {
  return {
    selbstabholung: an ? true : undefined,
    /* **Holt der Kunde ab, fährt keiner von uns mit** (Lisas Durchgang,
       08.10.2026, wie im Angebot: `bausteine/lieferwegregeln.ts`,
       `bedienerBeiAbholung`). Ein Gerät mit Wahl verliert „mit Bediener"
       samt gewähltem Bediener; ein Gerät mit Bedienerpflicht behält ihn —
       das ist ein Widerspruch, den ein Mensch klärt (`abholungGegenPflicht`).
       Beim Ausschalten kommt der Bediener nicht von selbst zurück. */
    ...(an && p.bedient && !bedienerMuss(p) ? { bedient: false, bediener: "" } : {}),
    zuschlaege: p.zuschlaege.map((z) => {
      if (an) {
        if (!z.faelltBeiAbholung || z.aus) return z;
        return { ...z, aus: true, ausWegenAbholung: true };
      }
      if (!z.ausWegenAbholung) return z;
      const { aus: _aus, ausWegenAbholung: _grund, ...rest } = z;
      return rest;
    }),
  };
}

/**
 * Der Anhänger als Wunsch für die Sammlung — ohne Betrag.
 *
 * Den holt die Karte selbst (`bausteine/Abholung.tsx`), aus derselben
 * Preisauskunft wie jedes andere Gerät. Hier steht nur, **welcher** Posten
 * entsteht: derselbe Zeitraum, ein Stück, ohne Bediener.
 */
/**
 * **Was „Kunde holt ab" mit dem Anhänger tut** (Prüfrunde Interview F,
 * N7-04, 05.10.2026).
 *
 * Bis hierher hakte der Schalter „Anhänger mitgeben" gleich mit an (B8,
 * „vorbelegt", 18.09.2026). Doris vermietete so einen Anhänger mit, den
 * niemand verlangt hatte — und ein reservierter Anhänger hielt den
 * Abschluss auf. Dass der Kunde einen Anhänger braucht, folgt aus „holt ab"
 * nicht (Regel 11: vorbelegt wird nur, was sich ableiten lässt). Jetzt:
 * **an** — die Frage steht da, ohne Haken (wie im Angebot: „Anhänger
 * mitgeben / Ohne Anhänger"); **aus** — ein mitgenommener Anhänger geht mit
 * weg, wenn ihn kein anderer Posten braucht.
 */
export function anhaengerNachUmlegen(an: boolean): "fragen" | "weg" {
  return an ? "fragen" : "weg";
}

export function anhaengerWunsch(p: Merkposten): Merkwunsch | null {
  if (!p.anhaenger) return null;
  return {
    wegenArt: "abholung",
    typId: p.anhaenger.typId,
    typ: p.anhaenger.typ,
    /* Der Name auf dem Beleg trägt die Ausstattung — „Baumaschinenanhänger
       3 t · mit Baumaschine". Ohne sie hieße die Zeile wie der Anhänger, den
       jemand allein mietet, und der kostet etwas anderes. */
    bezeichnung: p.anhaenger.variante
      ? `${p.anhaenger.typ} · ${p.anhaenger.variante}`
      : p.anhaenger.typ,
    varianteId: p.anhaenger.varianteId,
    variante: p.anhaenger.variante,
    von: p.von,
    bis: p.bis,
    wegen: p.id,
  };
}

/**
 * **Der Auflieger als Wunsch für die Sammlung** — ohne Betrag (0072).
 *
 * Wie beim Anhänger: Den Preis holt die Karte selbst, aus derselben
 * Preisauskunft wie bei jedem anderen Gerät. Hier steht nur, **welcher**
 * Posten entsteht — derselbe Zeitraum, ein Stück, ohne Bediener.
 *
 * **Ohne Ausstattung**, und das ist der Unterschied zum Anhänger: Dessen
 * Satz hängt an „mit Baumaschine" (0102), ein Auflieger hat seinen
 * Grundpreis. Wo ein Auflieger doch eine Ausstattung führt, gilt deren
 * Standard — dieselbe Regel wie bei jedem Posten aus der Trefferliste.
 */
export function aufliegerWunsch(p: Merkposten): Merkwunsch | null {
  if (!p.gespann) return null;
  return {
    wegenArt: "gespann",
    typId: p.gespann.typId,
    typ: p.gespann.typ,
    bezeichnung: p.gespann.typ,
    von: p.von,
    bis: p.bis,
    wegen: p.id,
  };
}

/** Der Posten, den ein Schalter an diesen hier gehängt hat — oder nichts. */
export function mitgebracht(
  posten: Merkposten[],
  p: Merkposten,
  art: "abholung" | "gespann",
): Merkposten | null {
  return posten.find((x) => x.wegen === p.id && x.wegenArt === art) ?? null;
}

/**
 * **Braucht noch jemand diesen Auflieger?** — dieselbe Frage wie beim
 * Anhänger, nur auf den anderen Schalter bezogen.
 */
export function aufliegerNochGebraucht(
  posten: Merkposten[],
  auflieger: Merkposten,
  ohne: string,
): boolean {
  return posten.some((x) =>
    x.id !== ohne
    && x.mitAuflieger === true
    && x.gespann?.typId === auflieger.typId
    && x.von === auflieger.von
    && x.bis === auflieger.bis);
}

/**
 * **Braucht noch jemand diesen Anhänger?**
 *
 * Zwei selbst abgeholte Maschinen im selben Zeitraum teilen sich den
 * vorgeschlagenen Anhänger — `merken` legt dieselbe Frage nur einmal ab.
 * Wer den Schalter an einer davon ausmacht, darf ihn der anderen nicht
 * wegnehmen. Wie viele Anhänger wirklich gebraucht werden, entscheidet die
 * Menge an der Zeile, und die setzt ein Mensch (Regel 11).
 */
export function anhaengerNochGebraucht(
  posten: Merkposten[],
  anhaenger: Merkposten,
  ohne: string,
): boolean {
  return posten.some((x) =>
    x.id !== ohne
    && x.selbstabholung === true
    && x.anhaenger?.varianteId === anhaenger.varianteId
    && x.von === anhaenger.von
    && x.bis === anhaenger.bis);
}

/* ══════════════════════════════════════════════════════════════════
   **„Kunde holt alles ab" — einmal für den ganzen Abschluss**
   (Prüfrunde Runde 11, „Neu gesehen", 05.10.2026).

   Am Telefon stand der Schalter nur im Blatt des einzelnen Postens: Wer drei
   Geräte „Ab Hof" vermietete, musste drei Blätter öffnen, sonst blieb
   „Jetzt vermieten" mit „Für Anlieferung fehlt der Preis" stehen. Am
   Schreibtisch standen die drei Schalter wenigstens im Abschluss selbst.
   Jetzt steht an beiden Stellen über den Posten **ein** Schalter für alle,
   bei denen es eine Frage ist (`abholungMoeglich`); die Schalter je Posten
   bleiben für den, der nur eines abholt.
   ══════════════════════════════════════════════════════════════════ */

/** Wie die Abholung über alle abholbaren Posten steht — `null`, wenn keiner abholbar ist. */
export function abholungAllerStand(posten: Merkposten[]): "alle" | "teils" | "keiner" | null {
  const moeglich = posten.filter(abholungMoeglich);
  if (!moeglich.length) return null;
  const an = moeglich.filter((p) => p.selbstabholung === true).length;
  return an === moeglich.length ? "alle" : an ? "teils" : "keiner";
}

/**
 * Was der Schalter für alle tut: je abholbaren Posten dieselbe Umlegung wie
 * der Schalter am Posten (`abholungUmlegen`), und beim Ausmachen gehen die
 * mitgebrachten Anhänger mit weg — keiner der Posten holt dann noch ab.
 * Beim Anmachen wird kein Anhänger angelegt; nach ihm fragt weiter der
 * Schalter am Posten (N7-04).
 */
export function abholungFuerAlle(
  posten: Merkposten[],
  an: boolean,
): { aendern: { id: string; teil: Teilposten }[]; entfernen: string[] } {
  const moeglich = posten.filter(abholungMoeglich);
  const aendern = moeglich
    .filter((p) => (p.selbstabholung === true) !== an)
    .map((p) => ({ id: p.id, teil: abholungUmlegen(p, an) }));
  const entfernen = an
    ? []
    : moeglich
      .map((p) => mitgebracht(posten, p, "abholung"))
      .filter((x): x is Merkposten => x !== null)
      .map((x) => x.id);
  return { aendern, entfernen: [...new Set(entfernen)] };
}

/* ══════════════════════════════════════════════════════════════════
   **„Kunde holt ab" im Kopf des Abschlusses** (Lisas Durchgang,
   08.10.2026). Lisa: *„Kann nicht mehr direkt ‚Kunde holt ab' setzen,
   sondern muss in die Position rein, das ist umständlich."*

   Der Kopf trägt **eine** Wahl für alle Posten mit Transport
   (`abholungMoeglich`) — auch bei einem einzigen. Ein Posten, der davon
   abweicht, sagt es an seiner Zeile und wird dort mit einem Klick wieder
   wie alle. Der Kopf ist kein eigenes Datum am Entwurf: Er folgt dem, was
   an den Posten steht (`abholungKopfNach`) — nur im gemischten Fall merkt
   er sich, was zuletzt für alle galt.
   ══════════════════════════════════════════════════════════════════ */

/** Muss das Gerät mit Bediener hinaus? (`fach.bedienerPflicht`, ohne Abhängigkeit.) */
function bedienerMuss(p: Pick<Merkposten, "bedienungspflicht">): boolean {
  return p.bedienungspflicht === "nur_mit_person";
}

/** Was der Kopf beim Öffnen zeigt: holt ab, wenn mehr als die Hälfte der Posten mit Transport abgeholt wird. */
export function abholungKopfVorgabe(posten: Merkposten[]): boolean {
  const moeglich = posten.filter(abholungMoeglich);
  const an = moeglich.filter((p) => p.selbstabholung === true).length;
  return moeglich.length > 0 && an * 2 > moeglich.length;
}

/** Der Kopf folgt den Posten: alle holen ab → an, keiner → aus, gemischt → wie bisher. */
export function abholungKopfNach(stand: ReturnType<typeof abholungAllerStand>, bisher: boolean): boolean {
  return stand === "alle" ? true : stand === "keiner" ? false : bisher;
}

/** Weicht dieser Posten von der Wahl im Kopf ab? Nur Posten mit Transport. */
export function abholungWeichtAb(p: Merkposten, kopf: boolean): boolean {
  return abholungMoeglich(p) && (p.selbstabholung === true) !== kopf;
}

/**
 * **Abgeholt, obwohl das Gerät nur mit Bediener hinausgeht** — offen, nicht
 * verboten (wie `abholungGegenPflicht` im Angebot). Die Zeile sagt es.
 */
export function abholungGegenPflichtPosten(p: Merkposten): boolean {
  return p.selbstabholung === true && bedienerMuss(p);
}

/**
 * **„Wohin? = Ab Hof" setzt „Kunde holt ab" als Vorgabe** — aber nur, wenn
 * jemand es wählt, nicht beim Vorbelegen: „Ab Hof" ist auch der leere Wert
 * für einen Kunden ohne Einsatzort, und eine still weggenommene Anlieferung
 * wäre eine Zahl, die fehlt, ohne dass es jemand merkt.
 */
export function abHofHoltAb(vorher: string, neu: string): boolean {
  return neu === "" && vorher !== "";
}
